const PRODUCT_TYPE_TOKENS = new Set(['box', 'piece', 'both', 'get']);

function normalizePricingToken(value) {
  return String(value).trim().toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_');
}

export function isProductTypeToken(value) {
  if (value == null || value === '') return false;
  return PRODUCT_TYPE_TOKENS.has(normalizePricingToken(value));
}

export function isPricingTypeToken(value) {
  if (value == null || value === '') return false;
  if (isProductTypeToken(value)) return false;
  const lower = normalizePricingToken(value);
  return (
    lower === 'offer' ||
    lower === 'offer_price' ||
    lower === 'sales' ||
    lower === 'sales_price' ||
    lower === 'standard' ||
    lower === 'standard_price' ||
    lower === 'off' ||
    value.toUpperCase() === 'OFFER' ||
    value.toUpperCase() === 'OFFER_PRICE' ||
    value.toUpperCase() === 'SALES' ||
    value.toUpperCase() === 'SALES_PRICE'
  );
}

export function toApiPromotionPricingType(formValue) {
  return formValue === 'offer' ? 'OFFER_PRICE' : 'SALES_PRICE';
}

export function fromApiPromotionPricingType(apiValue, fallback = 'sales') {
  if (apiValue == null || apiValue === '') {
    return fallback === 'offer' ? 'offer' : 'sales';
  }
  if (isProductTypeToken(apiValue)) {
    return fallback === 'offer' ? 'offer' : 'sales';
  }

  const raw = String(apiValue).trim();
  const lower = normalizePricingToken(raw);

  if (
    lower === 'offer' ||
    lower === 'offer_price' ||
    lower === 'off' ||
    raw.toUpperCase() === 'OFFER' ||
    raw.toUpperCase() === 'OFFER_PRICE' ||
    lower.includes('offer')
  ) {
    return 'offer';
  }
  if (
    lower === 'sales' ||
    lower === 'sales_price' ||
    lower === 'standard' ||
    lower === 'standard_price' ||
    raw.toUpperCase() === 'SALES_PRICE' ||
    raw.toUpperCase() === 'SALES' ||
    lower.includes('sales') ||
    lower.includes('standard')
  ) {
    return 'sales';
  }

  return fallback === 'offer' ? 'offer' : 'sales';
}

export function normalizePieceBoxPricingSelect(value, fallback = 'sales') {
  return fromApiPromotionPricingType(value, fallback);
}

function parsePromotionConfig(data = {}) {
  const raw = data.config ?? data.promotion_config ?? data.promotionConfig;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function pushPricingCandidate(candidates, value) {
  if (value == null) return;
  const text = String(value).trim();
  if (!text || text === 'null' || text === 'undefined') return;
  if (isProductTypeToken(text)) return;
  candidates.push(text);
}

export function collectMultiLayerBuyPricingCandidates(data = {}) {
  const config = parsePromotionConfig(data);
  const candidates = [];

  pushPricingCandidate(candidates, config.buy_pricing_type);
  pushPricingCandidate(candidates, config.buyPricingType);

  (data.buy_products || []).forEach((product) => {
    pushPricingCandidate(candidates, product.pricing_type);
    pushPricingCandidate(candidates, product.pricingType);
    pushPricingCandidate(candidates, product.buy_pricing_type);
    pushPricingCandidate(candidates, product.box_pricing_type);
    pushPricingCandidate(candidates, product.piece_pricing_type);
  });

  (data.slabs || []).forEach((slab) => {
    pushPricingCandidate(candidates, slab.buy_pricing_type);
    pushPricingCandidate(candidates, slab.box_pricing_type);
    pushPricingCandidate(candidates, slab.piece_pricing_type);
    (slab.buy_products || []).forEach((product) => {
      pushPricingCandidate(candidates, product.pricing_type);
      pushPricingCandidate(candidates, product.box_pricing_type);
      pushPricingCandidate(candidates, product.piece_pricing_type);
    });
  });

  pushPricingCandidate(candidates, data.buy_pricing_type);
  pushPricingCandidate(candidates, data.buyPricingType);
  pushPricingCandidate(candidates, data.buy_price_type);
  pushPricingCandidate(candidates, data.buy_pricing_type_name);

  return candidates;
}

export function resolveMultiLayerBuyPricingTypeFromApi(data = {}) {
  const candidates = collectMultiLayerBuyPricingCandidates(data);

  for (const candidate of candidates) {
    if (fromApiPromotionPricingType(candidate, null) === 'offer') {
      return 'offer';
    }
  }

  for (const candidate of candidates) {
    if (fromApiPromotionPricingType(candidate, 'sales') === 'sales') {
      return 'sales';
    }
  }

  return 'sales';
}

export function collectMultiLayerGetPricingCandidates(data = {}) {
  const config = parsePromotionConfig(data);
  const candidates = [];

  pushPricingCandidate(candidates, config.get_pricing_type);
  pushPricingCandidate(candidates, config.getPricingType);

  (data.get_products || []).forEach((product) => {
    pushPricingCandidate(candidates, product.pricing_type);
    pushPricingCandidate(candidates, product.pricingType);
    pushPricingCandidate(candidates, product.box_pricing_type);
    pushPricingCandidate(candidates, product.piece_pricing_type);
  });

  (data.slabs || []).forEach((slab) => {
    (slab.get_products || []).forEach((product) => {
      pushPricingCandidate(candidates, product.pricing_type);
      pushPricingCandidate(candidates, product.box_pricing_type);
      pushPricingCandidate(candidates, product.piece_pricing_type);
    });
  });

  pushPricingCandidate(candidates, data.get_pricing_type);
  pushPricingCandidate(candidates, data.getPricingType);

  return candidates;
}

export function resolveMultiLayerGetPricingTypeFromApi(data = {}) {
  const candidates = collectMultiLayerGetPricingCandidates(data);

  for (const candidate of candidates) {
    if (fromApiPromotionPricingType(candidate, null) === 'offer') {
      return 'offer';
    }
  }

  for (const candidate of candidates) {
    if (fromApiPromotionPricingType(candidate, 'sales') === 'sales') {
      return 'sales';
    }
  }

  return 'sales';
}

export function buildSlabGetQuantityCompulsoryErrors(slabs = []) {
  const errors = {};
  slabs.forEach((slab, index) => {
    if (!slab.hasGetProducts) return;
    const getQty = parseInt(slab.getQuantity, 10);
    if (isNaN(getQty) || getQty < 1) return;

    const compulsoryCount = (slab.getProducts || [])
      .filter((p) => p.compulsory)
      .reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 1), 0);

    if (compulsoryCount > getQty) {
      errors[`slab_${index}_getQuantity`] =
        `Compulsory quantity (${compulsoryCount}) cannot exceed get quantity (${getQty})`;
    }

    (slab.getProducts || []).forEach((product, pIndex) => {
      if (!product.compulsory) return;
      const productQty = parseInt(product.quantity, 10);
      if (!product.quantity || product.quantity === '' || isNaN(productQty) || productQty < 1) {
        errors[`slab_${index}_getProduct_${pIndex}_quantity`] =
          'Quantity is required for compulsory products';
      } else if (productQty > getQty) {
        errors[`slab_${index}_getProduct_${pIndex}_quantity`] =
          `Compulsory quantity cannot exceed get quantity (${getQty})`;
      }
    });
  });
  return errors;
}

export function applySlabGetQuantityCompulsoryErrors(prevErrors = {}, slabs = []) {
  const cleared = { ...prevErrors };
  Object.keys(cleared).forEach((key) => {
    if (/^slab_\d+_getQuantity$/.test(key) && String(cleared[key]).includes('Compulsory')) {
      delete cleared[key];
    }
    if (/^slab_\d+_getProduct_\d+_quantity$/.test(key)) {
      delete cleared[key];
    }
  });
  return { ...cleared, ...buildSlabGetQuantityCompulsoryErrors(slabs) };
}

export function resolveBuyProductPricingFields(product = {}, fallback = 'sales') {
  const pricingType = isPricingTypeToken(product.pricing_type)
    ? product.pricing_type
    : isPricingTypeToken(product.pricingType)
      ? product.pricingType
      : null;

  const boxPricingType = isPricingTypeToken(product.box_pricing_type)
    ? product.box_pricing_type
    : isPricingTypeToken(product.boxPricingType)
      ? product.boxPricingType
      : null;

  const piecePricingType = isPricingTypeToken(product.piece_pricing_type)
    ? product.piece_pricing_type
    : isPricingTypeToken(product.piecePricingType)
      ? product.piecePricingType
      : null;

  const boxPricingNorm = normalizePieceBoxPricingSelect(boxPricingType || pricingType, fallback);
  const piecePricingNorm = normalizePieceBoxPricingSelect(piecePricingType || pricingType, fallback);

  return {
    boxPricingType: boxPricingNorm,
    piecePricingType: piecePricingNorm,
    pricingType: boxPricingNorm,
  };
}
